import LoaderOverlay from "@/components/common/LoaderOverlay";
import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    SUMMARY_OPERATION_IMAGE_LAYER,
    SUMMARY_OPERATIONS_SOURCE,
} from "@/lib/map/layers";

import useMap from "@/lib/map/useMap";
import useRequest from "@/lib/request/useRequest";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { ListApplications } from "@/types/operations";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Table,
    TableHeader,
    TableBody,
    TableCell,
    TableColumn,
    getKeyValue,
    TableRow,
    Button,
    Checkbox,
    Spinner,
    SortDescriptor,
} from "@heroui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ImageSource } from "mapbox-gl";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

function SummaryTable({
    operationsQuery,
}: {
    operationsQuery: UseQueryResult<ListApplications>;
}) {
    const { formatUnit, formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("operations");
    const COLUMNS = [
        {
            key: "start_date",
            label: t("date"),
        },
        {
            key: "status",
            label: t("status"),
        },
        {
            key: "product",
            label: t("product"),
        },
        {
            key: "method",
            label: t("method"),
        },
        {
            key: "average_N",
            label: `${t("nitrogenRate")} (${formatUnit("lb_ac", "kg_ha")})`,
        },
        {
            key: "actions",
            label: t("actions"),
        },
    ];
    const { deleteOperations } = useFeatureFlag();
    const { userIsSentinel } = useAuth();
    const { fieldBbox, fieldId } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const [selectedOperationId, setSelectedOperationId] = useState<
        number | undefined
    >();

    const imageQuery = useGetRequest({
        url: `application_operations/${selectedOperationId}/map_display_image/`,
        responseType: "blob",
        enabled: !!selectedOperationId,
    });

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "start_date",
        direction: "ascending",
    });

    const operations = useMemo(() => {
        const ops = (operationsQuery.data?.operations ?? []).map(
            (operation) => {
                return {
                    ...operation,
                    start_date: formatDateMMDDYYYY(operation.start_date),
                    status: operation.is_complete ? "Completed" : "Pending",
                    product:
                        operation.application_products[0]?.product?.name ??
                        "--",
                    average_N: operation.average_N
                        ? roundTo(operation.average_N, 1)
                        : "--",
                };
            },
        );

        const totalRate = ops.reduce((acc, operation) => {
            return (
                acc +
                (typeof operation.average_N === "number"
                    ? operation.average_N
                    : 0)
            );
        }, 0);

        const { column, direction } = sortDescriptor;

        const sortedItems = !column
            ? ops
            : sortArrayByKey(
                ops,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                column as any,
                direction === "descending" ? "desc" : "asc",
            );

        sortedItems.push({
            id: "total",
            start_date: "Total",
            status: "",
            product: "",
            method: "",
            average_N: totalRate,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        return sortedItems.map((op, index) => ({ ...op, index }));
    }, [formatDateMMDDYYYY, operationsQuery.data?.operations, sortDescriptor]);

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox = selectedOperationId
            ? (invertedTurfBboxToFourCornerCoordinates(
                operations.find((op) => op.id === selectedOperationId)
                    ?.bounds,
            ) as Coordinates)
            : undefined;
        if (imageUrl) {
            const imSource = map.getSource<ImageSource>(
                SUMMARY_OPERATIONS_SOURCE,
            );
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(SUMMARY_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer(SUMMARY_OPERATION_IMAGE_LAYER);
            }
        } else {
            removeLayersIfExists(map, [SUMMARY_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [SUMMARY_OPERATIONS_SOURCE]);
        }
    }, [
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        operations,
        selectedOperationId,
    ]);

    const mutate = useMutateRequest({
        method: "delete",
        errorMessage: t("error.failed_to_delete_operation"),
        successMessage: t("success.operation_deleted_successfully"),
    });

    const confirm = useConfirm();

    const handleDelete = useCallback(
        async (operationId: number) => {
            confirm.showConfirm({
                title: "Delete Operation",
                description: "Are you sure you want to delete this operation?",
                color: "danger",
                confirmText: "Delete",
                onConfirm: async () => {
                    mutate.mutate(
                        {
                            url: `application_operations/${operationId}/`,
                        },
                        {
                            onSuccess: () => {
                                operationsQuery.refetch();
                            },
                        },
                    );
                },
            });
        },
        [confirm, mutate, operationsQuery],
    );

    return (
        <Table
            classNames={{
                td: "text-xs px-[0.25rem]",
                th: "text-xs px-[0.25rem] font-bold",
                tr: "text-xs px-[0.25rem]",
                wrapper: "p-[0.5rem]",
            }}
            aria-label="Operations Table"
            onSortChange={setSortDescriptor}
            sortDescriptor={sortDescriptor}
        >
            <TableHeader columns={COLUMNS}>
                {(column) => (
                    <TableColumn
                        key={column.key}
                        allowsSorting={column.key !== "actions"}
                    >
                        {column.label}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                emptyContent={"No operations found in this date range"}
                loadingContent={<Spinner label="Loading..." />}
                isLoading={operationsQuery.isLoading}
            >
                {operations.map((item) => {
                    if (item.id.toString() == "total") {
                        return (
                            <TableRow key="total">
                                {(columnKey) => {
                                    return (
                                        <TableCell
                                            key={columnKey}
                                            className="font-bold"
                                        >
                                            {getKeyValue(item, columnKey)}
                                        </TableCell>
                                    );
                                }}
                            </TableRow>
                        );
                    }

                    return (
                        <TableRow key={item.id}>
                            {(columnKey) => {
                                if (columnKey === "actions") {
                                    return (
                                        <TableCell key={columnKey}>
                                            <div className="flex flex-row gap-1 items-center">
                                                <LoaderOverlay
                                                    isLoading={
                                                        selectedOperationId ==
                                                        item.id &&
                                                        imageQuery.isFetching
                                                    }
                                                >
                                                    <Checkbox
                                                        aria-label="Display operation"
                                                        isSelected={
                                                            selectedOperationId ==
                                                            item.id
                                                        }
                                                        classNames={{
                                                            hiddenInput:
                                                                " inset-0",
                                                            label: " m-0",
                                                            base: "m-0",
                                                            wrapper: "m-0",
                                                        }}
                                                        onValueChange={() =>
                                                            setSelectedOperationId(
                                                                (id) =>
                                                                    item.id ==
                                                                        id
                                                                        ? undefined
                                                                        : item.id,
                                                            )
                                                        }
                                                    />
                                                </LoaderOverlay>
                                                <Button
                                                    isIconOnly
                                                    color="secondary"
                                                    className="tiny-icon-button"
                                                    aria-label="Edit Operation"
                                                    as={Link}
                                                    to="/dashboard/fields/$fieldId/operations/application/$applicationOperationId"
                                                    params={
                                                        {
                                                            fieldId,
                                                            applicationOperationId:
                                                                item.id,
                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                        } as any
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPencil}
                                                    />
                                                </Button>
                                                {(deleteOperations ||
                                                    userIsSentinel) && (
                                                        <Button
                                                            isIconOnly
                                                            color="danger"
                                                            className="tiny-icon-button"
                                                            onPress={() =>
                                                                handleDelete(
                                                                    item.id,
                                                                )
                                                            }
                                                            aria-label="Delete Operation"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                            />
                                                        </Button>
                                                    )}
                                            </div>
                                        </TableCell>
                                    );
                                }
                                return (
                                    <TableCell key={columnKey}>
                                        {getKeyValue(item, columnKey)}
                                    </TableCell>
                                );
                            }}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export default SummaryTable;
